/**
 * React Query hooks for LPA Types (Fee Basis Types and Waterfall Types)
 *
 * These hooks provide data fetching for LPA lookup tables used in
 * fund LPA terms configuration and custom investor agreements.
 */

import { useQuery } from '@tanstack/react-query';
import { fundService } from '@/services/fundService';
import type { FeeBasisType, WaterfallType } from '@/types/lpa-terms';

// Query key constants
const LPA_TYPES_QUERY_KEYS = {
  feeBasisTypes: ['lpa-types', 'fee-basis-types'] as const,
  activeFeeBasisTypes: ['lpa-types', 'fee-basis-types', 'active'] as const,
  waterfallTypes: ['lpa-types', 'waterfall-types'] as const,
  activeWaterfallTypes: ['lpa-types', 'waterfall-types', 'active'] as const,
};

/**
 * Hook to fetch only active fee basis types
 */
export function useActiveFeeBasisTypes() {
  return useQuery<FeeBasisType[], Error>({
    queryKey: LPA_TYPES_QUERY_KEYS.activeFeeBasisTypes,
    queryFn: () => fundService.getActiveFeeBasisTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutes - lookup data changes rarely
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to fetch only active waterfall types
 */
export function useActiveWaterfallTypes() {
  return useQuery<WaterfallType[], Error>({
    queryKey: LPA_TYPES_QUERY_KEYS.activeWaterfallTypes,
    queryFn: () => fundService.getActiveWaterfallTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutes - lookup data changes rarely
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}
